import type { Maske } from './wegweiser';

/**
 * Was jede Maske tut und wo sie im Ablauf steht (13.09.2026).
 *
 * ============================================================================
 * DER SATZ, AUS DEM DIESE DATEI ENTSTAND
 * ============================================================================
 *
 * Auftraggeber: „Es soll eine Hilfsfunktion für jede Maske geben. Hier soll die
 * Funktion der Maske beschrieben werden und in welchen Prozessen sie welchen
 * Schritt darstellt." Und dazu, wie sie ankommt: „Oder zusätzlich mit einer
 * kurzen Einleitung auf jeder Maske mit einem Hinweis zu mehr Infos?"
 *
 *   NFA-17  „Anwenderdokumentation und kontextbezogene Hilfe in der
 *           Oberfläche" (MUSS). Am FELD steht sie seit `Feld.tsx`; was hier
 *           dazukommt, ist die andere Hälfte — die Auskunft über die MASKE.
 *           Beide Hälften gehören zusammen: Wer weiß, was ein Feld will, weiß
 *           damit noch nicht, ob er auf der richtigen Seite steht.
 *
 * ============================================================================
 * DREI ANGABEN, UND DIE DRITTE IST DIE NÜTZLICHSTE
 * ============================================================================
 *
 * `einleitung` eröffnet die Hilfe — ein Satz, was man hier tut. Vom 13.09.2026
 * an stand er ohne Klick unter jedem Titel; seit ÄA-46 (Auftrag CRM-51) steht
 * er im Aufklapper unter dem Hilfe-Symbol der Kopfzeile, und die Seitenköpfe
 * sind ruhiger.
 *
 * `tut` sagt, was man hier erledigt. Zwei bis drei Sätze, keine Bedienanleitung
 * — wo welcher Knopf sitzt, sieht man; warum es ihn gibt, nicht.
 *
 * `nicht` sagt, was hier NICHT geht und wohin es gehört. Das ist die Angabe,
 * die im Betrieb die Zeit spart: Fast jede Rückfrage in diesem Haus lautet
 * „wo trage ich X ein" und nicht „wie bediene ich Y". Der Verweis führt mit
 * einem Klick dorthin.
 *
 * ============================================================================
 * VOLLSTÄNDIG, UND ZWAR ERZWUNGEN
 * ============================================================================
 *
 * `Record<Maske['art'], …>` — wer eine Maske hinzufügt und ihr keine Hilfe
 * gibt, bekommt keinen Übersetzer­lauf. Dieselbe Bauweise wie beim Wegweiser
 * (`WEGE`): Eine Aufzählung, die man vergessen kann, ist eine, die man
 * vergisst — und dann steht die neue Maske als einzige ohne Erklärung da,
 * ohne dass es jemandem auffällt.
 *
 * ============================================================================
 * WARUM NICHT AN JEDEM FELD EIN FRAGEZEICHEN
 * ============================================================================
 *
 * Die Felder haben ihre Hilfe schon: `feld__hilfe` steht als Satz UNTER dem
 * Feld, ungefragt und im Blick. Ein Symbol, das denselben Satz hinter einen
 * Klick legt, macht die Auskunft schlechter und die Maske unruhiger. Das
 * Fragezeichen beantwortet die andere Frage — die nach der MASKE, und die
 * stellt man einmal und nicht je Feld.
 */

/** Ein Verweis auf eine andere Maske — der Text sagt, was dort geschieht. */
export type Verweis = {
  readonly text: string;
  /** Wohin es gehört. Fehlt sie, ist es ein Hinweis ohne Ziel. */
  readonly maske?: Maske;
};

export type Maskenhilfe = {
  /** Der Satz, mit dem die Hilfe beginnt: was man hier tut (ÄA-46). */
  readonly einleitung: string;
  /** Zwei bis drei Sätze: was diese Maske erledigt und warum es sie gibt. */
  readonly tut: readonly string[];
  /** Was hier NICHT geht — mit dem Weg dorthin, wo es hingehört. */
  readonly nicht?: readonly Verweis[];
};

/**
 * Ein Ablauf des Hauses als Kette von Masken.
 *
 * ============================================================================
 * DIE KETTE IST DIE AUSKUNFT, NICHT DIE EINZELNE MASKE
 * ============================================================================
 *
 * „In welchen Prozessen stellt sie welchen Schritt dar" — die Frage lässt sich
 * an einer Maske allein nicht beantworten, und eine Antwort, die in jeder
 * Maske einzeln stünde, liefe auseinander. Deshalb stehen die Abläufe HIER,
 * einmal, und die Hilfe rechnet daraus, wo man ist: Was davor kam, was folgt,
 * und der wievielte Schritt es ist.
 *
 * EINE MASKE DARF IN MEHREREN KETTEN STEHEN. Der Termin ist das Ende des
 * Verkaufs und der Anfang des Betriebstags; das ist keine Doppelung, sondern
 * die Stelle, an der die beiden Abläufe sich berühren.
 */
export type Prozess = {
  readonly name: string;
  /** Wozu der Ablauf da ist — ein Satz über der Kette. */
  readonly zweck: string;
  readonly schritte: readonly {
    readonly art: Maske['art'];
    /** Was in diesem Schritt geschieht — kurz, im Infinitiv. */
    readonly was: string;
  }[];
};

export const PROZESSE: readonly Prozess[] = [
  {
    name: 'Vom Angebot zur Rechnung',
    zweck: 'Der Weg eines Auftrags durch das Haus — vom ersten Gespräch bis zum Geldeingang.',
    schritte: [
      { art: 'kundenakte', was: 'Den Kunden anlegen oder finden' },
      { art: 'angebotsakte', was: 'Angebot schreiben und versenden' },
      { art: 'optionsanfragen', was: 'Termine bei der Disposition anfragen und halten lassen' },
      { art: 'auftragsakte', was: 'Auftrag aus dem angenommenen Angebot' },
      { art: 'termine', was: 'Termine für den Auftrag anlegen' },
      { art: 'fakturierungsliste', was: 'Was fakturiert werden kann' },
      { art: 'rechnungsakte', was: 'Rechnung stellen und versenden' },
      { art: 'offeneposten', was: 'Zahlungseingang und Mahnstand' },
    ],
  },
  {
    name: 'Der Betriebstag',
    zweck: 'Wie aus Terminen ein Tag wird, der läuft — und was danach zurückkommt.',
    schritte: [
      { art: 'termine', was: 'Termine des Tages' },
      { art: 'trainingsplanung', was: 'Bausteine auf Blöcke legen, Ressourcen zuteilen' },
      { art: 'besetzung', was: 'Trainer anfragen und zusagen lassen' },
      { art: 'empfang', was: 'Am Kurstag: Plätze klären und abhaken' },
    ],
  },
  {
    name: 'Die Buchung',
    zweck: 'Was zwischen dem verkauften Platz und dem Menschen am Tresen liegt.',
    schritte: [
      { art: 'buchungsvorgang', was: 'Buchung finden, umbuchen, stornieren' },
      { art: 'arbeitsliste', was: 'Fehlende Teilnehmernamen vor der Frist' },
      { art: 'absagen', was: 'Nach einer Terminabsage: umbuchen oder erstatten' },
      { art: 'widerrufe', was: 'Eingegangene Widerrufe entscheiden' },
      { art: 'empfang', was: 'Am Kurstag: wer da ist' },
    ],
  },
  {
    name: 'Was ein Training braucht',
    zweck: 'Die Stammdaten, ohne die ein Tag sich nicht planen lässt.',
    schritte: [
      { art: 'artikelakte', was: 'Das Produkt mit Preis und Vertriebsform' },
      { art: 'bausteine', was: 'Die Übungen, aus denen es besteht' },
      { art: 'assets', was: 'Module, Räume und Fahrzeuge, die sie brauchen' },
      { art: 'trainerstamm', was: 'Wer es fährt — mit Qualifikation und Vertrag' },
      { art: 'honorare', was: 'Was der Einsatz kostet' },
    ],
  },
];

/*
 * ============================================================================
 * DIE HILFE JE MASKE
 * ============================================================================
 *
 * Der Ton: Sätze, keine Stichworte. Wer Hilfe liest, ist gerade unsicher — und
 * Stichworte setzen voraus, was man ihm erklären will.
 */
export const MASKENHILFE: Record<Maske['art'], Maskenhilfe> = {
  arbeitsplatz: {
    einleitung: 'Was heute ansteht und was liegen geblieben ist.',
    tut: [
      'Der Einstieg in den Tag: offene Aufgaben, die nächsten Termine und die Zahlen, '
        + 'an denen man merkt, ob etwas aus dem Ruder läuft.',
      'Jede Kachel führt dorthin, wo die Sache bearbeitet wird — die Übersicht selbst '
        + 'ändert nichts.',
      // SYS-12 i. d. F. AeA-40 (Auftrag #35)
      'Unter „Dashboard anpassen" legen Sie Widgets und Berichte in ein Raster aus vier '
        + 'Spalten: am Griff ziehen, an der Ecke vergrößern, oder alles über das Menü am Feld. '
        + 'Die Anordnung gilt für Ihr Konto auf jedem Gerät.',
      // Auftrag CRM-170, AeA-70
      '„Das steht an" zeigt, was im Haus zu tun ist; „Alle Aufgaben" klappt die ganze Liste auf. '
        + 'Die Glocke oben rechts zeigt dagegen nur Ihre eigenen fälligen Wiedervorlagen, Optionen und Angebote. '
        + 'Die Einstellungen finden Sie im Menü unter System → Verwaltung.',
    ],
  },

  /* Kunden ------------------------------------------------------------- */
  kundenliste: {
    einleitung: 'Alle Kunden des Hauses, such- und filterbar.',
    tut: [
      'Hier finden Sie einen Kunden über Namen, Nummer oder Ort und legen neue an.',
      'Die Liste ist der Einstieg; gearbeitet wird in der Akte dahinter.',
    ],
  },
  kundeAnlegen: {
    einleitung: 'Einen neuen Kunden aufnehmen — Firma oder Privatperson.',
    tut: [
      'Die Kundenart entscheidet über fast alles Weitere: Preise, Zahlungsbedingungen '
        + 'und welche Verträge zulässig sind.',
      'Doppelte Anlagen fängt die Maske ab, bevor sie entstehen.',
    ],
    nicht: [
      { text: 'Zwei Akten desselben Kunden zusammenführen', maske: { art: 'kundenliste' } },
    ],
  },
  kundenakte: {
    einleitung: 'Alles zu einem Kunden an einer Stelle.',
    tut: [
      'Stammdaten, Ansprechpartner, Belege, Termine und Buchungen dieses Kunden.',
      'Von hier aus beginnt fast jeder Vorgang: ein Angebot, ein Auftrag, eine Buchung.',
      'Anschrift, Telefon und E-Mail stehen in einer Karte am Namen oben: Sie öffnet, wenn die Maus '
        + 'kurz auf dem Namen ruht, oder über das Adressbuch-Zeichen daneben. Escape schließt sie.',
      /* BUC-122 (Auftrag CRM-140) */
      'Der Reiter Buchungen nennt je Buchung den Betrag, wie er in der Buchung steht — auch bei '
        + 'einer stornierten. Die Zahl der Teilnehmer steht in der Buchung selbst.',
      /* DAT-12, BUC-145 (AeA-64) */
      'Hat der Kunde ein Guthaben, wird es hier erstattet oder mit einer anderen offenen Rechnung '
        + 'desselben Kunden verrechnet — auch als Teilbetrag. Beides fragt nach und lässt sich '
        + 'nicht zurücknehmen.',
      /* LEG-12 (Auftrag CRM-173) */
      'Werbliche Ansprache setzt eine Einwilligung oder einen zulässigen Ausnahmetatbestand voraus; '
        + 'ohne Eintrag wird nicht geworben, und das System weist eine Werbenachricht ab. „Werbestand '
        + 'dokumentieren" verlangt eine Quelle. Ein Widerspruch wirkt kanalübergreifend — für Post, '
        + 'Mail und Telefon zugleich. Zurückgenommen wird er mit einer neuen Einwilligung samt Quelle; '
        + 'der bisherige Stand bleibt im Änderungsprotokoll lesbar.',
      'Jeder Ansprechpartner hat bei dieser Firma einen eigenen Werbestand („Werbestand …" an seinem '
        + 'Kärtchen); sein privater Stand wirkt hier nicht. Ein Widerspruch der Firma gilt für alle '
        + 'Ansprechpartner, auch wenn sie selbst eingewilligt haben.',
    ],
    nicht: [
      {
        text: 'Dauerhafte Nachlässe für diesen Kunden vereinbaren',
        maske: { art: 'rabattkonditionen' },
      },
      { text: 'Seine offenen Rechnungen und ihren Mahnstand sehen', maske: { art: 'offeneposten' } },
    ],
  },
  zusammenfuehren: {
    einleitung: 'Zwei Akten desselben Kunden zu einer machen.',
    tut: [
      'Belege, Termine und Buchungen wandern auf die bleibende Akte; die andere wird '
        + 'stillgelegt und bleibt auffindbar.',
      'Der Vorgang ist nicht umkehrbar — deshalb zeigt die Maske vorher, was wohin geht.',
    ],
  },

  webanfragen: {
    einleitung: 'Anfragen aus den Formularen des Auftritts: Kontakt, Rückruf, Firmenanfrage, Bewerbung.',
    tut: [
      'Eine Anfrage legt keinen Kunden an. Die Prüfung auf Dubletten schlägt beim Eingang vor, '
        + 'wem sie gehören könnte — über E-Mail, Telefon und Name mit PLZ.',
      'Jede Anfrage wird einmal entschieden: einem Kunden zuordnen, erledigen oder mit Grund verwerfen.',
    ],
  },

  kontozuordnungen: {
    einleitung: 'Neue Online-Konten und der Kunde, zu dem sie gehören.',
    tut: [
      'Mit der Bestätigung der Adresse ordnet das System ein Konto dem Kunden zu, der diese '
        + 'Adresse führt — am Kunden oder an einem Ansprechpartner —, wenn es genau einer ist.',
      'Führen mehrere Kunden die Adresse, steht der Fall hier: einem Treffer zuordnen oder mit '
        + 'Grund verwerfen. Jede Zuordnung lässt sich mit Grund zurücknehmen; derselbe Kunde '
        + 'wird dem Konto danach nicht wieder von selbst zugeordnet.',
    ],
  },

  /* Produkte ----------------------------------------------------------- */
  artikelliste: {
    einleitung: 'Der Katalog: Trainings, Events, Verpflegung und Waren.',
    tut: [
      'Jeder Artikel, der auf einem Beleg stehen kann, steht hier — mit Preisen, '
        + 'Vertriebsformen und dem, was er an Ressourcen braucht.',
    ],
    nicht: [
      {
        text: 'Steuersatz und Erlöskonto einer ganzen Gruppe vorgeben',
        maske: { art: 'warengruppen' },
      },
    ],
  },
  artikelAnlegen: {
    einleitung: 'Ein neues Produkt in den Katalog aufnehmen.',
    tut: [
      'Produktart und Mengeneinheit legen fest, wie der Artikel verkauft und geplant '
        + 'wird; beides lässt sich später nur schwer ändern.',
      'Ohne gepflegten Preis lässt sich der Artikel nicht auf einen Beleg nehmen.',
    ],
  },
  artikelakte: {
    einleitung: 'Ein Produkt mit Preisen, Bausteinen und Bedarf.',
    tut: [
      'Was das Training kostet, woraus es besteht und was es an Modulen, Räumen und '
        + 'Trainern verlangt.',
      'Die Bausteinfolge hier ist die Vorlage für jeden Tagesplan, auf dem dieses '
        + 'Produkt läuft.',
    ],
    nicht: [
      { text: 'Den Katalog der Bausteine selbst pflegen', maske: { art: 'bausteine' } },
      { text: 'Preise über alle Artikel hinweg ändern', maske: { art: 'preislisten' } },
    ],
  },
  warengruppen: {
    einleitung: 'Die Ordnung des Katalogs — und was sie vererbt.',
    tut: [
      'Warengruppen fassen Artikel zusammen und geben Steuersatz und Erlöskonto vor, '
        + 'solange der Artikel nichts eigenes sagt.',
    ],
  },
  preislisten: {
    einleitung: 'Preise mit Gültigkeitsdatum, je Vertriebsform und Standort.',
    tut: [
      'Eine Preisliste gilt ab einem Tag. Wer Preise ändert, legt eine neue Fassung an, '
        + 'statt die alte zu überschreiben — sonst ist ein Beleg vom Vorjahr nicht mehr '
        + 'nachvollziehbar.',
    ],
    nicht: [
      { text: 'Einem einzelnen Kunden einen Nachlass geben', maske: { art: 'rabattkonditionen' } },
    ],
  },
  rabattkonditionen: {
    einleitung: 'Nachlässe, Staffeln und was ein Kunde dauerhaft bekommt.',
    tut: [
      'Konditionen gelten je Kunde, Gruppe oder Artikel und greifen automatisch, wenn '
        + 'eine Position entsteht.',
    ],
  },
  bausteine: {
    einleitung: 'Die Übungen, aus denen ein Training zusammengesetzt wird.',
    tut: [
      'Jeder Baustein sagt, wie lange er dauert, in welche Phase er gehört und was er '
        + 'an Modul, Raum oder Fahrzeug braucht.',
      'Was hier gepflegt ist, kann der Tagesplan verplanen — was fehlt, findet er nicht.',
    ],
    nicht: [
      {
        text: 'Festlegen, aus welchen Bausteinen ein Produkt besteht',
        maske: { art: 'artikelliste' },
      },
      { text: 'Module, Räume und Fahrzeuge selbst anlegen', maske: { art: 'assets' } },
    ],
  },
  honorare: {
    einleitung: 'Was ein Trainereinsatz kostet — als datierte Tabelle.',
    tut: [
      'Sätze je Leistungsart und Tarif, gültig ab einem Tag. Es gilt immer genau eine '
        + 'allgemeine Tabelle; daneben stehen die Vereinbarungen mit einzelnen Trainern.',
      'Eine leere Zelle in der Tabelle eines Trainers fällt auf die allgemeine zurück.',
    ],
    nicht: [
      { text: 'Einen Trainer anlegen oder seinen Vertrag pflegen', maske: { art: 'trainerstamm' } },
    ],
  },

  /* Angebote und Aufträge ----------------------------------------------- */
  angebotsliste: {
    einleitung: 'Alle Angebote mit Stand und Frist.',
    tut: ['Was offen ist, was angenommen wurde und was abläuft.'],
  },
  angebotAnlegen: {
    einleitung: 'Ein Angebot für einen Kunden schreiben.',
    tut: [
      'Positionen kommen aus dem Katalog; Preis und Steuersatz zieht das System aus '
        + 'der gültigen Preisliste.',
    ],
  },
  angebotsakte: {
    einleitung: 'Ein Angebot bearbeiten, versenden und in einen Auftrag überführen.',
    tut: [
      'Solange es offen ist, lässt sich alles ändern. Mit dem Versand entsteht eine '
        + 'Fassung, die bleibt — spätere Änderungen ergeben eine neue.',
      'Die Annahme macht daraus einen Auftrag; das Angebot bleibt als Beleg stehen.',
    ],
    nicht: [
      { text: 'Den Preis eines Artikels dauerhaft ändern', maske: { art: 'preislisten' } },
      { text: 'Termine für die angebotene Leistung anlegen', maske: { art: 'termine' } },
    ],
  },
  optionsanfragen: {
    einleitung: 'Terminwünsche aus Angeboten beantworten: zusagen, ablehnen, anders vorschlagen.',
    tut: [
      'Der Vertrieb fragt an seinem Angebot Termine an; hier steht, was noch offen ist — '
        + 'mit Wunschterminen, Teilnehmerzahl, Eingang und Restfrist.',
      'Eine Zusage hält die Plätze bis zu einer Frist. Sie ist kürzer als die Gültigkeit '
        + 'des Angebots, weil sie Kapazität bindet: Läuft sie ab, verfällt das Angebot.',
      'Nichts verschwindet aus dieser Liste, bevor jede Alternative beantwortet ist.',
    ],
    nicht: [
      { text: 'Einen Termin anlegen, den es noch nicht gibt', maske: { art: 'termine' } },
      { text: 'Trainer und Module für den Tag verplanen', maske: { art: 'trainingsplanung' } },
    ],
  },

  auftragsliste: {
    einleitung: 'Alle Aufträge mit Stand und Fortschritt.',
    tut: ['Was bestätigt ist, was läuft und was zur Rechnung ansteht.'],
  },
  auftragAnlegen: {
    einleitung: 'Einen Auftrag ohne vorheriges Angebot anlegen.',
    tut: [
      'Der kurze Weg für das, was am Telefon vereinbart wurde. Aus einem angenommenen '
        + 'Angebot entsteht der Auftrag dagegen von selbst.',
    ],
  },
  rechnungAnlegen: {
    einleitung: 'Eine Rechnung ohne vorheriges Angebot: zuerst der Auftrag, dann die Rechnung.',
    tut: [
      '„Anlegen und abrechnen" legt den Auftrag an, bestätigt ihn und erzeugt den '
        + 'Rechnungsentwurf in einem Zug. Der Auftrag bleibt mit der Rechnung verbunden.',
      'Festgeschrieben wird im Entwurf — mit Rückfrage. Bis dahin lässt er sich verwerfen.',
    ],
    nicht: [
      { text: 'Einen Auftrag mit Terminen und Rabatten erfassen', maske: { art: 'auftragAnlegen' } },
      { text: 'Bestätigte Aufträge abrechnen', maske: { art: 'fakturierungsliste' } },
    ],
  },
  auftragsakte: {
    einleitung: 'Ein Auftrag mit Positionen, Terminen und Rechnungsstand.',
    tut: [
      'Der Auftrag ist die Vereinbarung: Was ist verkauft, zu welchem Preis, wann findet '
        + 'es statt.',
      'Jede Änderung nach der Bestätigung bleibt in der Historie sichtbar.',
    ],
    nicht: [
      { text: 'Den Tag planen, an dem die Termine laufen', maske: { art: 'trainingsplanung' } },
      { text: 'Die Rechnung zu diesem Auftrag stellen', maske: { art: 'fakturierungsliste' } },
    ],
  },
  fakturierungsliste: {
    einleitung: 'Was abgerechnet werden kann — und noch nicht ist.',
    tut: [
      'Durchgeführte Leistungen, fällige Anzahlungen und Aufträge, die eine Rechnung '
        + 'erwarten. Die Liste ist der Einstieg in die Faktura.',
    ],
    nicht: [
      { text: 'Eine gestellte Rechnung ändern oder stornieren', maske: { art: 'rechnungsliste' } },
      { text: 'Offene Posten und Mahnstand sehen', maske: { art: 'offeneposten' } },
    ],
  },
  rahmenliste: {
    einleitung: 'Rahmenaufträge: vereinbarte Mengen über einen Zeitraum.',
    tut: ['Was ein Kunde über das Jahr abnimmt und wie viel davon schon abgerufen ist.'],
  },
  rahmenAnlegen: {
    einleitung: 'Eine Rahmenvereinbarung mit einem Kunden anlegen.',
    tut: ['Menge, Zeitraum und Konditionen, aus denen später einzelne Abrufe entstehen.'],
  },
  rahmenakte: {
    einleitung: 'Ein Rahmenauftrag mit seinen Abrufen.',
    tut: ['Was vereinbart war, was abgerufen wurde und was noch offen ist.'],
  },

  /* Rechnungen --------------------------------------------------------- */
  rechnungsliste: {
    einleitung: 'Alle Ausgangsrechnungen mit Stand und Zahlung.',
    tut: ['Was gestellt, versendet, bezahlt oder storniert ist.'],
  },
  rechnungsakte: {
    einleitung: 'Eine Rechnung mit Positionen, Versand und Zahlungsstand.',
    tut: [
      'Eine versendete Rechnung wird nicht mehr geändert — eine Korrektur ist eine '
        + 'Gutschrift oder eine Storno­rechnung.',
      /* DAT-12, BUC-145, FIB-25 (AeA-64) */
      'Ein Guthaben nach Storno oder Gutschrift wird erstattet oder mit einer anderen offenen '
        + 'Rechnung desselben Kunden verrechnet, nie über das Guthaben oder den offenen Betrag hinaus. '
        + 'Ausgezahlt wird außerhalb des CRM; beides geht an Navision, und solange dort die Zuordnung '
        + 'fehlt, wartet die Übertragung sichtbar.',
    ],
    nicht: [
      { text: 'Offene Posten und Mahnstand sehen', maske: { art: 'offeneposten' } },
      { text: 'Ändern, wie der Beleg aussieht', maske: { art: 'belegvorlagen' } },
    ],
  },
  offeneposten: {
    einleitung: 'Was noch nicht bezahlt ist, nach Fälligkeit.',
    tut: [
      'Offene Beträge, überfällige Rechnungen und ihr Mahnstand.',
      /* RNG-25, E-39: Gemahnt wird in Navision; das CRM zeigt Stufe, Datum und Gebühr. */
      'Gemahnt wird in Navision. Stufe, Datum und Gebühr kommen mit dem täglichen Abgleich.',
      'Zahlungen kommen ebenfalls aus Navision; von Hand vermerkt wird in der Rechnung.',
      /* DAT-12, BUC-145 (AeA-64) */
      'Ein negativer Betrag ist ein Guthaben: Es wird an der Zeile erstattet oder mit einer anderen '
        + 'offenen Rechnung desselben Kunden verrechnet.',
    ],
    nicht: [
      { text: 'Eine neue Rechnung erzeugen', maske: { art: 'fakturierungsliste' } },
    ],
  },
  bgAbrechnung: {
    einleitung: 'Angerechnete Gutscheine sammeln und je Kostenträger abrechnen.',
    tut: [
      /* PRD-38, E-70 */
      'Die Teilnehmerrechnung trägt den vollen Preis mit voller Umsatzsteuer; der Gutschein mindert nur den Zahlbetrag. '
        + 'Die Abrechnung an den Kostenträger ist deshalb ein Beleg ohne erneute Umsatzsteuer.',
      'Kostenträger und Zeitraum sind frei wählbar; ohne Zeitraum erscheinen alle offenen Gutscheine.',
      'Der Zahlungseingang setzt die Abrechnung auf bezahlt; ein Storno gibt ihre Gutscheine wieder frei.',
    ],
    nicht: [
      { text: 'Einen Gutschein erfassen — das geschieht am Auftrag oder an der Buchung', maske: { art: 'auftragsliste' } },
    ],
  },
  bestaetigungen: {
    einleitung: 'Versandte Bestätigungen und was davon noch aussteht.',
    tut: ['Welcher Beleg an welche Adresse ging — und was nicht zugestellt werden konnte.'],
  },

  /* Berichte (AUS-16, Auftrag #24) ------------------------------------- */
  berichtsliste: {
    einleitung: 'Ihre gespeicherten Berichte — ein Klick öffnet einen im Baukasten.',
    tut: [
      'Jede Kachel rechnet ihren Bericht beim Öffnen der Liste neu, mit Ihren heutigen '
        + 'Rechten. Ein Bericht speichert nur, WAS gezeigt wird — nie die Zahlen selbst.',
      'In diesem Schritt sind Berichte privat. Teilen mit Team oder allen, Vorlagen und '
        + 'Kacheln fürs Dashboard folgen.',
    ],
    nicht: [
      { text: 'Die Tageskennzahlen des Arbeitsplatzes ansehen', maske: { art: 'arbeitsplatz' } },
    ],
  },
  berichtNeu: {
    einleitung: 'Kennzahl wählen, gruppieren, filtern — das Ergebnis steht sofort daneben.',
    tut: [
      /* Auftrag CRM-54: links nur das Gewählte, der Katalog im Pop-up. */
      'Links steht nur, was im Bericht ist: Kennzahlen, Gruppierung und Aufteilung, je mit × zum '
        + 'Entfernen. „Kennzahl hinzufügen", „Gruppierung wählen" und „Aufteilung wählen" öffnen ein '
        + 'Fenster mit Suche und dem ganzen Katalog; was nicht passt, ist ausgegraut und sagt warum. '
        + 'Kennzahlen wählen Sie dort nacheinander und schließen mit „Fertig" oder Esc, eine '
        + 'Gruppierung oder Aufteilung schließt das Fenster gleich. Rechts Zeitraum, Filter und die '
        + 'Definition der Kennzahl.',
      'Der laufende Monat ist schraffiert: Er ist noch nicht abgeschlossen. '
        + 'Summen enthalten nur, was Sie auch im Einzelnen sehen dürfen.',
      '„Speichern" legt den Bericht privat ab, „Als CSV" gibt das Ergebnis mit '
        + 'Definition, Filter, Zeitraum, Zeitpunkt und Ihrem Namen im Kopf aus.',
      /* Auftrag #54 (KI-15): nur, wenn die Verwaltung die Funktion eingeschaltet hat. */
      '„In eigenen Worten" macht aus einer Frage einen Bericht des Baukastens — gekennzeichnet '
        + 'als KI-Vorschlag. Die Zahlen rechnet das CRM; was der Katalog nicht kann, steht als Hinweis da. '
        + 'Nachfragen verfeinern ihn, „Verwerfen" stellt den Baukasten zurück.',
      '„Erläuterung" schreibt einen kurzen Kommentar zum berechneten Bericht — einen Entwurf zur '
        + 'Prüfung, der nie automatisch versendet wird. Hinaus gehen nur die verdichteten Zahlen.',
    ],
    nicht: [{ text: 'Einen gespeicherten Bericht öffnen', maske: { art: 'berichtsliste' } }],
  },
  berichtsakte: {
    einleitung: 'Ein gespeicherter Bericht — ändern, speichern, umbenennen oder löschen.',
    tut: [
      'Geöffnet wird die gespeicherte Zusammenstellung; gerechnet wird mit dem Stand von '
        + 'jetzt und Ihren heutigen Rechten.',
      'Änderungen gelten erst mit „Speichern"; „Änderungen verwerfen" kehrt zum '
        + 'gespeicherten Stand zurück.',
    ],
    nicht: [{ text: 'Zurück zu allen Berichten', maske: { art: 'berichtsliste' } }],
  },

  /* Betrieb ------------------------------------------------------------ */
  betriebspruefung: {
    einleitung:
      'Was eine Betriebsprüfung sehen will — an einer Stelle statt verstreut in den '
      + 'Fachmasken.',
    tut: [
      'Vier Auskünfte: was noch festzuschreiben ist und bis wann; welche internen '
        + 'Kontrollen eingerichtet sind und wie oft sie liefen; was an Steuerschlüsseln, '
        + 'Nummernkreisen und Belegvorlagen geändert wurde, mit altem Wert; und wer in '
        + 'einem Zeitraum welche Rechte hatte.',
      'Über jedem Bericht steht, ab wann das Änderungsprotokoll trägt. Für die Zeit '
        + 'davor kann er nichts aussagen — und „nichts" darf sich nicht lesen wie „es '
        + 'ist nichts passiert".',
    ],
    nicht: [
      { text: 'Eine Rechnung festschreiben', maske: { art: 'rechnungsliste' } },
      { text: 'Rollen und Rechte ändern', maske: { art: 'benutzerliste' } },
    ],
  },
  termine: {
    einleitung: 'Alle Termine des Hauses — als Liste und als Kalender.',
    tut: [
      'Ein Termin ist ein Training an einem Tag zu einer Zeit. Er entsteht aus einem '
        + 'Auftrag oder als offener Termin mit Einzelplatzverkauf.',
      'Hier wird er angelegt, verlegt oder abgesagt.',
      'Die Warteliste steht am Training, nicht am Termin. Am Termin sehen Sie, wie '
        + 'viele bestätigte Einträge zu ihm passen. Wird ein Platz frei, bekommen sie '
        + 'von selbst eine Mail mit Buchungslink — ein Platz wird dabei nicht gehalten.',
    ],
    nicht: [
      { text: 'Den Ablauf des Tages planen', maske: { art: 'trainingsplanung' } },
      { text: 'Trainer anfragen', maske: { art: 'besetzung' } },
    ],
  },
  terminakte: {
    einleitung: 'Ein Termin mit allem, was an ihm hängt: Zeit, Ort, Trainer, Teilnehmende.',
    tut: [
      'Die Teilnehmerliste führt zu Buchung, Auftrag und Kunde.',
      'Ein gebuchter Platz ohne Namen wird hier benannt. Am Exklusivtermin erfassen Sie '
        + 'weitere Teilnehmende für den Kunden der Vereinbarung; über der vereinbarten '
        + 'Kapazität wird das ausgewiesen, nicht abgewiesen.',
      'Der Check-in geht hier wie am Empfang — auch am Exklusivtermin.',
    ],
    nicht: [
      { text: 'Einen Namen gegen einen anderen tauschen', maske: { art: 'buchungsvorgang' } },
      { text: 'Den Ablauf des Tages planen', maske: { art: 'trainingsplanung' } },
    ],
  },
  trainingsplanung: {
    einleitung: 'Den Betriebstag planen: was läuft wann, auf welchem Modul, mit wem.',
    tut: [
      'Der Ablauf folgt aus den Bausteinen des Produkts; die Ressourcen teilt der Plan '
        + 'zu oder Sie von Hand.',
      'Konflikte halten die Arbeit nicht auf — sie stehen als Liste daneben. Erst die '
        + 'Freigabe verlangt, dass keiner mehr offen ist.',
    ],
    nicht: [
      { text: 'Einen Trainer für den ganzen Termin anfragen', maske: { art: 'besetzung' } },
      { text: 'Termine anlegen oder verlegen', maske: { art: 'termine' } },
    ],
  },
  besetzung: {
    einleitung: 'Trainer für Termine anfragen und ihre Antwort abwarten.',
    tut: [
      'Wer passt, wer kann, wer hat zugesagt. Eine Anfrage bekommt eine Frist; läuft sie '
        + 'ab, geht sie an den nächsten.',
      'Ein Termin kann verkauft werden, bevor die Zusage steht — offen heißt nicht ungeplant.',
    ],
    nicht: [
      { text: 'Bausteine und Module des Tages verteilen', maske: { art: 'trainingsplanung' } },
      { text: 'Festlegen, was der Einsatz kostet', maske: { art: 'honorare' } },
    ],
  },
  empfang: {
    einleitung: 'Der Kurstag am Tresen: wer kommt, wer fehlt, wer noch keinen Namen hat.',
    tut: [
      'Je Termin jeder gebuchte Platz. Die Plätze ohne Namen stehen oben — sie sind die '
        + 'Arbeit, alles andere ist Abhaken.',
    ],
    nicht: [
      { text: 'Teilnehmernamen zu einer Buchung nachtragen', maske: { art: 'arbeitsliste' } },
    ],
  },
  buchungsvorgang: {
    einleitung: 'Eine Buchung finden, anlegen und bearbeiten.',
    tut: [
      'Umbuchen, stornieren, Teilnehmer austauschen, Gebühren ausrechnen.',
      // Auftrag CRM-70
      'Ohne geöffnete Buchung steht hier die Liste der Buchungen, die Termine ab heute '
        + 'zuerst. Zeitraum, Stand, Training und Name filtern sie wahlweise; die Filter '
        + 'stehen in der Adresse und bleiben beim Neuladen und beim Zurück erhalten. '
        + 'Eine geöffnete Buchung zeigt nur sich selbst — der Menüpunkt „Buchungen" oder '
        + 'die Zurück-Taste des Browsers führt mit denselben Filtern in die Liste.',
      'Mit der Nummer vom Beleg öffnet „Öffnen" die Buchung direkt. Ohne Nummer sucht '
        + '„Buchung suchen" über Namen von Kunde oder Teilnehmer, Produkt, Zeitraum und Status.',
      'Beim Anlegen werden Kunde und Produkt in einer Suchmaske gewählt, der Termin im '
        + 'Kalender. Der Preis steht da, sobald alle drei feststehen. Die Teilnehmer können '
        + 'gleich mit erfasst werden — ist der Anmelder selbst dabei, genügt ein Haken.',
      'Das Gespräch endet nicht an einer Modulgrenze: Ein neuer Kunde wird im Suchfenster '
        + 'angelegt. Ein Termin ohne genug Platz steht im Kalender da und sagt, warum.',
      // Auftrag #36
      'Eine geöffnete Buchung ist eine Akte mit Reitern: Übersicht, Teilnehmer, '
        + 'Zusatzleistungen, Storno und Umbuchung, Beleg und Zahlung, Verlauf und Bedingungen. '
        + 'Eine Marke am Reiter zeigt, wo etwas offen ist.',
    ],
    nicht: [
      { text: 'Festlegen, was ein Storno kostet', maske: { art: 'gebuehrenstaffeln' } },
      { text: 'Fehlende Namen vor der Frist nachhalten', maske: { art: 'arbeitsliste' } },
    ],
  },
  // Auftrag CRM-175
  buchungAnlegen: {
    einleitung: 'Eine Buchung anlegen — für einen Kunden, ein Produkt und einen Termin.',
    tut: [
      'Kunde und Produkt werden in einer Suchmaske gewählt, der Termin im Kalender. Der Preis '
        + 'steht da, sobald alle drei feststehen. Die Teilnehmer können gleich mit erfasst '
        + 'werden — ist der Anmelder selbst dabei, genügt ein Haken.',
      'Nach dem Buchen öffnet sich die neue Buchung. Abbrechen, der Menüpunkt „Buchungen" und '
        + 'die Zurück-Taste führen in die Liste, mit den Filtern, die sie hatte.',
    ],
    nicht: [
      { text: 'Eine bestehende Buchung suchen oder öffnen', maske: { art: 'buchungsvorgang' } },
    ],
  },
  widerrufe: {
    einleitung: 'Eingegangene Widerrufe — was noch zu entscheiden oder zu bestätigen ist.',
    tut: [
      'Einen Widerruf per Mail oder Brief mit dem Zeitpunkt seines Zugangs erfassen; die '
        + 'Eingangsbestätigung geht automatisch hinaus.',
      'Anerkennen storniert die Buchung ohne Gebühr, die Erstattung läuft über den Storno. '
        + 'Ablehnen verlangt eine Begründung. Beides ist endgültig.',
    ],
    nicht: [
      { text: 'Eine Buchung stornieren, die der Kunde ohne Widerruf zurückgibt', maske: { art: 'buchungsvorgang' } },
    ],
  },
  wiedervorlagen: {
    einleitung: 'Die Wiedervorlagen, die Ihnen oder einer Ihrer Rollen zugewiesen sind — standardmäßig die fälligen.',
    tut: [
      'Eine Wiedervorlage öffnen, mit Vermerk erledigen oder wieder öffnen. Fällig ist, was heute oder '
        + 'früher fällig ist oder dessen Erinnerung erreicht ist.',
      // Auftrag CRM-170, AeA-70
      'Dieselben fälligen Wiedervorlagen stehen auch in der Glocke oben rechts; ein Klick dort öffnet sie hier.',
      'Mit dem Recht, Kunden zu ändern: Fälligkeit, Zuständigen, Priorität und Erinnerung ändern. Jede '
        + 'Änderung steht im Verlauf.',
    ],
    nicht: [
      { text: 'Eine Wiedervorlage zu einem Kunden anlegen — das geht in der Kundenakte', maske: { art: 'kundenliste' } },
    ],
  },
  rezensionen: {
    einleitung: 'Die Google-Rezensionen der Standorte — standardmäßig die offenen.',
    tut: [
      'Eine Rezension öffnen und beantworten, eine Antwort ändern oder löschen. Die Antwort geht '
        + 'über den Ausgang an Google; die Maske zeigt, ob sie gesendet ist.',
      'Höchstens 4096 Zeichen je Antwort. Was beim Senden scheitert, wird wiederholt und lässt '
        + 'sich von Hand erneut anstoßen.',
    ],
    nicht: [
      { text: 'Einem Standort sein Google-Profil zuordnen', maske: { art: 'standorte' } },
    ],
  },
  posteingang: {
    einleitung: 'Die Mails an Info und Vertrieb — je Anliegen ein Vorgang mit eigener Nummer, neueste zuerst.',
    tut: [
      /* Auftrag CRM-185: eigener Menüpunkt, Reiter nach Abteilung vorbelegt. */
      'Die Postfächer stehen als eigener Menüpunkt direkt nach der Übersicht. Wer im Vertrieb und '
        + 'nicht im Service arbeitet, beginnt im Postfach Vertrieb, alle anderen in Info; jeder Reiter '
        + 'bleibt wählbar.',
      'Einen Vorgang öffnen und den Verlauf lesen, den Status setzen oder ihn wieder als ungelesen '
        + 'markieren. Das gilt nur im CRM; im Postfach selbst bleibt alles unverändert.',
      /* Auftrag CRM-188: Vorgang im ganzen Fenster, eigene Adresse, Rückweg zur selben Liste. */
      'Der Vorgang öffnet im ganzen Fenster: links der Verlauf, rechts die Bearbeitung. „← Postfächer" '
        + 'oder die Zurück-Taste des Browsers führt zur Liste mit denselben Filtern zurück; die Pfeile '
        + 'oben blättern zum vorigen oder nächsten Vorgang der Liste. Jeder Vorgang hat eine eigene Adresse.',
      'Externe Bilder laden erst auf Klick. Anhänge öffnen sich im Browser oder werden gespeichert, '
        + 'gesperrte Dateitypen gar nicht.',
      'Unter „Aussortiert" liegen Werbung und automatische Antworten ohne Vorgang; eine fälschlich '
        + 'aussortierte Nachricht lässt sich als Vorgang übernehmen.',
      '„Meine" zeigt die eigenen Vorgänge samt denen, die man gerade vertritt; „Warteschlange" die '
        + 'ohne Zuständigen. Je Zeile stehen Zuständig, Fälligkeit und die Restzeit bis zum nächsten Ziel.',
    ],
    nicht: [
      { text: 'Die Postfächer einrichten', maske: { art: 'postfaecher' } },
      { text: 'Textbausteine pflegen', maske: { art: 'textbausteine' } },
    ],
  },
  absagen: {
    einleitung: 'Die Buchungen abgesagter Termine — je Termin eine Karte, je Buchung eine Zeile.',
    tut: [
      'Kontakt vermerken, auf einen Alternativtermin umbuchen oder stornieren und erstatten — '
        + 'jeweils ohne Gebühr, denn das Haus hat abgesagt.',
      'Eine Absage ist erst erledigt, wenn keine Buchung mehr offen ist. Was länger als die '
        + 'eingestellte Frist offen bleibt, wird der Leitung gemeldet.',
    ],
    nicht: [
      { text: 'Einen Termin absagen', maske: { art: 'termine' } },
      { text: 'Eine einzelne Buchung stornieren, die der Kunde zurückgibt', maske: { art: 'buchungsvorgang' } },
    ],
  },
  arbeitsliste: {
    einleitung: 'Buchungen, bei denen Teilnehmernamen fehlen — nach Frist sortiert.',
    tut: [
      'Ein Firmenkunde bucht fünf Plätze und reicht die Namen nach. Diese Liste sagt, '
        + 'bei wem die Frist drängt.',
    ],
    nicht: [
      { text: 'Am Kurstag klären, wer vor einem steht', maske: { art: 'empfang' } },
    ],
  },

  /* Ressourcen --------------------------------------------------------- */
  assets: {
    einleitung: 'Module, Räume, Fahrzeuge und Betriebsmittel.',
    tut: [
      'Was das Haus an Flächen und Geräten hat, wo es steht, wie viele Gruppen darauf '
        + 'passen und welche Übungen es trägt.',
      'Eine Sperrzeit nimmt eine Ressource vorübergehend aus der Planung.',
    ],
    nicht: [
      { text: 'Festlegen, welche Übung welches Modul braucht', maske: { art: 'bausteine' } },
      { text: 'Eine Ressource für einen Tag zuteilen', maske: { art: 'trainingsplanung' } },
    ],
  },
  trainerstamm: {
    einleitung: 'Trainer mit Qualifikation, Standorten und Vertrag.',
    tut: [
      'Wer was fahren darf, an welchen Standorten er eingesetzt wird und ob eine '
        + 'Auftragsverarbeitungsvereinbarung vorliegt.',
      'Ohne diese Vereinbarung sieht ein externer Trainer weder den Tagesplan noch seine '
        + 'Teilnehmerliste.',
    ],
    nicht: [
      { text: 'Einen Trainer für einen Termin anfragen', maske: { art: 'besetzung' } },
      { text: 'Seine Sätze pflegen', maske: { art: 'honorare' } },
    ],
  },

  /* Verwaltung --------------------------------------------------------- */
  belegvorlagen: {
    einleitung: 'Wie Angebot, Auftrag und Rechnung aussehen und was darauf steht.',
    tut: ['Briefkopf, Textbausteine und Fußzeile der ausgegebenen Belege.'],
  },
  postfaecher: {
    einleitung: 'Die Mailpostfächer des Hauses und was aus ihnen ankommt.',
    tut: ['Eingang, Zuordnung und der Stand der Verbindung.'],
  },
  checkingeraete: {
    einleitung: 'Die Tablets für den Selbst-Check-in am Veranstaltungsort.',
    tut: [
      'Ein Tablet je Eingang anlegen, einem Standort zuordnen und bei Verlust sperren.',
      'Der Schlüssel erscheint genau einmal — danach trägt die Datenbank nur seinen Streuwert.',
      'Am Tablet selbst: die Seite checkin.html öffnen, Kennung und Schlüssel eingeben, Kioskmodus einschalten.',
    ],
  },
  portalzugaenge: {
    einleitung: 'Wer die Buchungsschnittstelle benutzen darf.',
    tut: [
      'Zugänge für das Buchungsportal einrichten, begrenzen und schließen.',
      'Der Schlüssel erscheint genau einmal — danach trägt die Datenbank nur seinen Streuwert.',
      'Produktiv und Test sind zwei Zugänge: Nur der Testzugang darf Fehlerfälle anfordern.',
    ],
  },
  navision: {
    einleitung: 'Der Abgleich mit der Finanzbuchhaltung.',
    tut: [
      'Welche Belege übertragen wurden, welche hängen und woran es lag.',
      'Was hier liegen bleibt, fehlt drüben in der Buchhaltung.',
    ],
  },
  berichtsversand: {
    einleitung: 'Welche Berichte nach Zeitplan an wen gehen — und was dabei scheiterte.',
    tut: [
      'Alle Abos des Hauses mit Rhythmus, Empfängern und dem nächsten Lauf.',
      'Die letzten Läufe je Empfänger: versandt, übersprungen oder gescheitert, mit Grund.',
    ],
    nicht: [{ text: 'Den Versand eines Berichts ändern — das tut sein Eigentümer im Bericht', maske: { art: 'berichtsliste' } }],
  },
  erechnung: {
    einleitung: 'Ausgang und Prüfung elektronischer Rechnungen.',
    tut: ['Was als XRechnung erzeugt, versendet und bestätigt wurde.'],
  },
  ki: {
    einleitung: 'Der Zugang zum Sprachmodell — und warum die KI gerade läuft oder ruht.',
    tut: [
      'Global, je Mandant, je Rolle und je Funktion ein- und ausschalten; Modell, Aufwand und '
        + 'Höchstbetrag je Monat festlegen.',
      'Eingeschaltet wird nur mit bestätigtem Auftragsverarbeitungsvertrag. Der Schlüssel steht '
        + 'allein in der Betriebskonfiguration.',
      'Je Funktion und Monat: wie viele Vorschläge unverändert, bearbeitet oder gar nicht '
        + 'übernommen wurden, und was sie gekostet haben.',
    ],
  },
  geschaeftsdaten: {
    einleitung: 'Die Angaben des Hauses, die auf jedem Beleg stehen.',
    tut: ['Firmierung, Anschrift, Steuernummern und Bankverbindung.'],
  },
  gebuehrenstaffeln: {
    einleitung: 'Was ein Storno oder eine Umbuchung kostet — nach Vorlauf.',
    tut: [
      'Die Staffeln gelten je Vertragsart und rechnen sich aus dem Abstand zum Termin.',
      'Geändert wird mit Datum: Eine Buchung von gestern rechnet nach der Staffel von '
        + 'gestern.',
    ],
  },
  rechtstexte: {
    einleitung: 'AGB, Widerruf und Datenschutzhinweise in ihren Fassungen.',
    tut: [
      'Jede Fassung gilt ab einem Tag. Ein Beleg zeigt die Fassung, die bei seiner '
        + 'Entstehung galt — deshalb wird nichts überschrieben.',
    ],
  },
  textbausteine: {
    einleitung: 'Wiederkehrende Antworten für den Posteingang, je Postfach und Kategorie.',
    tut: [
      'Bausteine mit Platzhaltern wie {{kunde.name}} oder {{termin.datum}} anlegen, ändern, abschalten '
        + 'und entfernen. Beim Antworten stehen die aktiven zur Wahl, die zum Vorgang passen.',
    ],
    nicht: [
      { text: 'Auf einen Vorgang antworten', maske: { art: 'posteingang' } },
      { text: 'Reaktions- und Lösungsziele der Postfächer setzen', maske: { art: 'postfaecher' } },
    ],
  },
  datenschutz: {
    einleitung: 'Auskunft, Berichtigung und Löschung für betroffene Personen.',
    tut: [
      'Datenschutzanfragen — Auskunft, Löschung, Einschränkung — mit Frist und Nachweis. Was gelöscht werden darf und was '
        + 'die Aufbewahrung hält, entscheidet die Maske nicht allein.',
    ],
  },
  einstellungen: {
    einleitung: 'Die Stellschrauben des Systems.',
    tut: ['Fristen, Vorlaufzeiten und Regeln, die die Planung und die Belege steuern.'],
    nicht: [
      { text: 'Storno- und Umbuchungsgebühren festlegen', maske: { art: 'gebuehrenstaffeln' } },
      { text: 'AGB und Datenschutzhinweise fassen', maske: { art: 'rechtstexte' } },
    ],
  },
  standorte: {
    einleitung: 'Die Standorte des Hauses mit Anschrift und Bundesland.',
    tut: [
      'Die Bezeichnung steht an jedem Termin des Standorts, auch in der Buchungsschnittstelle.',
      'Die Kennung bleibt, wie sie ist: An ihr hängen Preise, Termine und Betriebstage.',
      'Abgeschaltet wird statt gelöscht — und erst, wenn dort keine Termine mehr anstehen.',
    ],
    nicht: [{ text: 'Schließtage eines Standorts eintragen', maske: { art: 'kalender' } }],
  },
  kalender: {
    einleitung: 'Gesetzliche Feiertage je Bundesland und Schließtage je Standort.',
    tut: [
      'Feiertage verschieben die Zahlungsfrist einer Rechnung auf den nächsten Werktag.',
      'An einem Schließtag entsteht am Standort kein Termin; schon geplante Termine nehmen keine '
        + 'Buchung mehr an. Die Maske nennt sie beim Eintragen.',
      'Reicht der Kalender weniger als zwölf Monate voraus, meldet es auch die Systemübersicht.',
    ],
  },
  uebernahme: {
    einleitung: 'Daten aus dem Altsystem einlesen.',
    tut: [
      'Jeder Lauf zeigt vorher, was er anlegen und was er liegen lassen würde, und '
        + 'hinterlässt eine Klärungsliste.',
    ],
  },
  benutzerliste: {
    einleitung: 'Wer Zugang zum System hat.',
    tut: ['Konten, Rollen und ihr Stand — angelegt, gesperrt, ohne Kennwort.'],
  },
  benutzerakte: {
    einleitung: 'Ein Benutzerkonto mit Rolle, Rechten und Anmeldedaten.',
    tut: [
      'Die Rolle entscheidet, was jemand sieht und darf. Rechte einzeln zu vergeben ist '
        + 'nicht vorgesehen — dafür gibt es die festen Rollen.',
    ],
    nicht: [
      { text: 'Qualifikation und Vertrag eines Trainers pflegen', maske: { art: 'trainerstamm' } },
    ],
  },
  systemzustand: {
    einleitung: 'Läuft alles? Warteschlangen, Hintergrundläufe und Fehler.',
    tut: ['Was im Hintergrund arbeitet, was liegen geblieben ist und seit wann.'],
    nicht: [
      { text: 'Belege ansehen, die in der Buchhaltung fehlen', maske: { art: 'navision' } },
    ],
  },
  meinkonto: {
    einleitung: 'Ihr eigenes Konto: Kennwort, zweiter Faktor, Farben.',
    tut: ['Was nur Sie betrifft — und nur Sie ändern können.'],
    nicht: [
      {
        text: 'Das Kennwort eines anderen Benutzers zurücksetzen',
        maske: { art: 'benutzerliste' },
      },
    ],
  },

  /* Was kein eigener Gegenstand ist ------------------------------------ */
  bereichsseite: {
    einleitung: 'Die Punkte dieser Gruppe mit einem Satz zu jedem.',
    tut: [
      'Eine Zwischenseite: Sie sagt, was hinter jedem Punkt steckt, damit man nicht '
        + 'raten muss, welcher gemeint ist.',
    ],
  },
  unfertig: {
    einleitung: 'Diesen Bereich gibt es noch nicht.',
    tut: [
      'Der Punkt steht im Menü, weil er zum Bild gehört — gebaut ist er nicht. Die Seite '
        + 'sagt, was dort einmal stehen wird.',
    ],
  },
};

/**
 * Die Abläufe, in denen diese Maske vorkommt — mit ihrer Stelle darin.
 *
 * Eine Maske kann in mehreren stehen (der Termin ist das Ende des Verkaufs und
 * der Anfang des Tages). Steht sie in keinem, ist das kein Mangel: Die
 * Benutzerverwaltung ist ein Ort und kein Schritt.
 */
/**
 * Wohin der Verweis eines Schritts führt (Auftrag CRM-118, L7).
 *
 * EIN SCHRITT NENNT EINE MASKENART, KEINE AKTE. „Den Kunden anlegen oder
 * finden" meint keinen bestimmten Kunden — vor CRM-118 wurde daraus eine
 * Kundenakte ohne Kennung, und die Akte holte `/kunden/undefined`. Wo die Art
 * eine Kennung braucht, führt der Schritt in die Liste, in der man die Akte
 * findet. Die Stelle „Sie sind hier" rechnet weiter mit der Art.
 *
 * `undefined` heißt: Diesen Schritt kann kein Verweis tragen; er bleibt Text.
 */
export function schrittziel(art: Maske['art']): Maske | undefined {
  switch (art) {
    case 'kundenakte':
    case 'zusammenfuehren':
      return { art: 'kundenliste' };
    case 'artikelakte':
      return { art: 'artikelliste' };
    case 'angebotsakte':
      return { art: 'angebotsliste' };
    case 'auftragsakte':
      return { art: 'auftragsliste' };
    case 'rahmenakte':
      return { art: 'rahmenliste' };
    case 'rechnungsakte':
      return { art: 'rechnungsliste' };
    case 'terminakte':
      return { art: 'termine' };
    case 'berichtsakte':
      return { art: 'berichtsliste' };
    case 'benutzerakte':
      return { art: 'benutzerliste' };
    case 'unfertig':
    case 'bereichsseite':
      return undefined;
    default:
      /* Alle übrigen Arten tragen keine Pflichtkennung — die Art allein ist die Maske. */
      return { art } as Maske;
  }
}

export function ablaeufeZu(art: Maske['art']): readonly {
  readonly prozess: Prozess;
  readonly stelle: number;
}[] {
  return PROZESSE.flatMap((prozess) => {
    const stelle = prozess.schritte.findIndex((s) => s.art === art);
    return stelle === -1 ? [] : [{ prozess, stelle }];
  });
}
